import { ENGINES } from "../config/engines";

/**
 * Lenguaje oficial de Commercial Operator — fuente única para textos que
 * deben decir exactamente lo mismo en cualquier pantalla donde aparezcan
 * (auditoría de nomenclatura, agosto 2026). Un concepto, una palabra, en
 * todas partes: nunca un componente inventa su propia traducción de un
 * mismo dato.
 */

// Mismos 4 estados que ya usa OpportunityDetail (OUTCOME_BUTTONS) para
// registrar un resultado — esta es la fuente que ambos, la fila de tabla y
// el panel de detalle, deben leer. "Cerrado sin respuesta" (no
// "Sin respuesta") a propósito: ResponseBadge ya usa "Sin respuesta" /
// "Esperando respuesta" para el estado de conversación TODAVÍA abierto —
// usar la misma frase aquí para un resultado CERRADO confundía ambas cosas
// en la misma fila de Mi cartera (hallazgo real de la auditoría).
export const OUTCOME_LABELS = {
  won: "Ganado",
  in_progress: "En negociación",
  lost: "Perdido",
  no_response: "Cerrado sin respuesta",
};

const plural = (days) => `${days} día${days === 1 ? "" : "s"}`;

/**
 * `windowDays` no significa lo mismo en todos los motores (verificado en
 * código, no asumido): en Renovación es un vencimiento real
 * (`daysUntilRenewal`); en Recuperación y Venta cruzada es una ventana
 * sintética de urgencia recomendada por la heurística
 * (adapters/intelligence/heuristic.js) — no existe un vencimiento real ahí.
 * Por eso el texto cambia según el motor en vez de usar "Vence en" siempre.
 *
 * `compact`: en el panel de detalle, la etiqueta de sección ya dice "Vence
 * en" / "Tiempo para actuar" (timeSectionLabel) justo arriba de esta
 * insignia — repetir el verbo ahí sería redundante ("VENCE EN" / "Vence en
 * 14 días"). En la tabla de Mi cartera no hay ese contexto verbal junto a
 * cada fila (el encabezado de columna es genérico, "Tiempo para actuar",
 * para ambos tipos de motor) — ahí la frase completa es la que se
 * entiende sola, sin depender de leer el encabezado.
 */
export function timeBadgeLabel(engineId, days, { compact = false } = {}) {
  if (days == null) return "—";
  if (compact) return plural(days);
  return engineId === "renewal" ? `Vence en ${plural(days)}` : `Actuar en ${plural(days)}`;
}

export function timeSectionLabel(engineId) {
  return engineId === "renewal" ? "Vence en" : "Tiempo para actuar";
}

// Reemplaza el breadcrumb crudo de la URL (mostraba slugs en inglés como
// "wallet"/"closures"/"turn" en cada pantalla salvo Hoy) por el mismo
// nombre humano que ya aprobó el usuario en Sidebar.jsx. Los motores se
// derivan de ENGINES para no duplicar sus nombres en un segundo lugar.
const STATIC_ROUTE_LABELS = {
  "/turn": "trabajo de hoy",
  "/wallet": "mi cartera",
  "/closures": "cierres",
  "/executive": "panorama ejecutivo",
  "/forecast": "proyección de ingresos",
  "/ingestion": "cargar cartera",
  "/settings": "configuración",
};

export function breadcrumbFor(pathname) {
  if (pathname === "/") return "hoy";
  if (STATIC_ROUTE_LABELS[pathname]) return STATIC_ROUTE_LABELS[pathname];
  const engine = ENGINES.find((e) => e.path === pathname);
  if (engine) return engine.humanLabel.toLowerCase();
  // Respaldo honesto para cualquier ruta no mapeada todavía — nunca debería
  // alcanzarse en producción si este mapa se mantiene al día con App.js.
  return pathname.replace(/^\//, "").replace(/-/g, " ");
}
